import { lazy, Suspense } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Hero } from "@/components/sections/Hero";
import { HeroTrustStack } from "@/components/sections/HeroTrustStack";
import { ClientLogoMarquee } from "@/components/ClientLogoMarquee";
import { AsSeenInVillanytt } from "@/components/sections/AsSeenInVillanytt";

// Below-the-fold — lazy-loadas för att kapa initial JS-bundle och main-thread work.
const EnergyCalculator = lazy(() => import("@/components/sections/EnergyCalculator").then(m => ({ default: m.EnergyCalculator })));
const HeroTrustBar = lazy(() => import("@/components/sections/HeroTrustBar").then(m => ({ default: m.HeroTrustBar })));
const Testimonials = lazy(() => import("@/components/sections/Testimonials").then(m => ({ default: m.Testimonials })));
const LiveTrustTicker = lazy(() => import("@/components/sections/LiveTrustTicker").then(m => ({ default: m.LiveTrustTicker })));
const TrustLogoBar = lazy(() => import("@/components/sections/TrustLogoBar").then(m => ({ default: m.TrustLogoBar })));
const LogoMarquee = lazy(() => import("@/components/LogoMarquee").then(m => ({ default: m.LogoMarquee })));
const Brands = lazy(() => import("@/components/sections/Brands").then(m => ({ default: m.Brands })));
const ProcessSteps = lazy(() => import("@/components/sections/ProcessSteps").then(m => ({ default: m.ProcessSteps })));
const BentoWhy = lazy(() => import("@/components/sections/BentoWhy").then(m => ({ default: m.BentoWhy })));
const Services = lazy(() => import("@/components/sections/Services").then(m => ({ default: m.Services })));
const SofiasAnglar = lazy(() => import("@/components/sections/SofiasAnglar").then(m => ({ default: m.SofiasAnglar })));

import { IMAGES } from "@/lib/images";


const HOMEPAGE_FAQS = [
  { q: "Vad gör BBN Nordic?", a: "Vi monterar professionell fönsterfilm åt företag, fastighetsbolag och privatpersoner i Sverige: solfilm, säkerhetsfilm och dekorfilm. Vi är auktoriserad 3M-installatör och auktoriserad Solar Screen-installatör, har funnits sedan 2008 och har 3 500+ nöjda kunder." },
  { q: "Vilka områden täcker ni?", a: "Vi arbetar i hela Skåne (Malmö, Lund, Helsingborg, Kristianstad, Ystad, Trelleborg, Landskrona, Staffanstorp m.fl.) samt Göteborg med kringliggande kommuner. Vi tar också större entreprenader i övriga Sverige upp till Stockholm. Etableringskostnad kan tillkomma beroende på projektets omfattning och plats." },
  { q: "Hur snabbt får jag en offert?", a: "Du får offerten snart efter platsbesöket. Vi mäter kostnadsfritt och ger dig ett fast pris skriftligt, så att du kan fundera innan du bestämmer dig." },
  { q: "Vilken garanti gäller?", a: "Du får 10 års produktgaranti från 3M på materialet och 10 års arbetsgaranti på installationen. För Solar Screen Silver-serien gäller 10 års tillverkargaranti även utvändigt." },
  { q: "Påverkar fönsterfilm garantin på mina fönster?", a: "Nej, inte när rätt filmtyp väljs för glaset. Före montering på 3-glas och lågemissionsglas gör vi ett termiskt stresstest och en glasanalys så att både glas- och filmgaranti gäller." },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "BBN Nordic — Auktoriserad 3M-installatör för fönsterfilm i Sverige" },
      { name: "description", content: "Auktoriserad 3M- och Solar Screen-installatör sedan 2008. Solfilm, säkerhetsfilm och dekorfilm för företag, fastigheter och privat — i Skåne, Göteborg, Stockholm och hela södra Sverige. 10 års garanti." },
      { property: "og:title", content: "BBN Nordic — Auktoriserad 3M-installatör för fönsterfilm i Sverige" },
      { property: "og:description", content: "Sedan 2008 levererar vi 3M och Solar Screen fönsterfilm till företag, BRF och privatpersoner i hela södra Sverige. Kostnadsfri offert, 10 års garanti." },
      { property: "og:url", content: "https://bbnnordic.se" },
      { property: "og:image", content: IMAGES.hero },
    ],
    links: [
      { rel: "canonical", href: "https://bbnnordic.se" },
      { rel: "preload", as: "image", href: IMAGES.hero, fetchPriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: HOMEPAGE_FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: f.a,
              speakable: { "@type": "SpeakableSpecification", cssSelector: [".faq-answer"] },
            },
          })),
          speakable: { "@type": "SpeakableSpecification", cssSelector: [".faq-answer"] },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Installation av professionell fönsterfilm",
          name: "Solfilm, säkerhetsfilm & dekorfilm — auktoriserad 3M-installation",
          provider: { "@type": "LocalBusiness", name: "BBN Nordic AB", url: "/" },
          areaServed: [
            { "@type": "AdministrativeArea", name: "Skåne" },
            { "@type": "City", name: "Göteborg" },
          ],
          brand: [{ "@type": "Brand", name: "3M" }, { "@type": "Brand", name: "Solar Screen" }, { "@type": "Brand", name: "NoTrax" }],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Fönsterfilm-tjänster",
            itemListElement: [
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Solfilm för företag", url: "/solfilm-foretag" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Solfilm för privatperson", url: "/solfilm-privat" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Säkerhetsfilm", url: "/sakerhetsfilm" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Dekorfilm & frostfilm", url: "/dekorfilm" } },
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Så installeras professionell fönsterfilm av BBN Nordic",
          description:
            "Fyrstegsprocess för installation av 3M och Solar Screen fönsterfilm: kostnadsfri mätning, fast offert kort därefter, auktoriserad montering och 10 års produktgaranti.",
          totalTime: "P21D",
          supply: [{ "@type": "HowToSupply", name: "Professionell fönsterfilm (3M eller Solar Screen)" }],
          tool: [{ "@type": "HowToTool", name: "Certifierad monteringsutrustning" }],
          step: [
            {
              "@type": "HowToStep",
              position: 1,
              name: "Kostnadsfri mätning på plats",
              text: "Lokal montör kommer ut, mäter varje fönster och pratar igenom vad du vill få bort — värme, bländning eller insyn. Tar oftast 30–60 minuter.",
            },
            {
              "@type": "HowToStep",
              position: 2,
              name: "Skriftlig snabb offert",
              text: "Du får ett fast pris på mejlen. Tackar du nej kostar det ingenting.",
            },
            {
              "@type": "HowToStep",
              position: 3,
              name: "Auktoriserad montering",
              text: "Bokas inom 2–3 veckor. 1–2 dagar på plats. Auktoriserade 3M-montörer monterar filmen enligt tillverkarens anvisningar. Du kan vara hemma som vanligt.",
            },
            {
              "@type": "HowToStep",
              position: 4,
              name: "10 års produktgaranti + 10 års arbetsgaranti",
              text: "Skriftlig garanti från 3M på materialet och BBN Nordic på arbetet. Vi följer upp efter 12 månader.",
            },
          ],
        }),
      },
    ],
  }),
  component: Index,
});

function Index() {
  return (
    <>
      <Hero />
      <ClientLogoMarquee />
      <HeroTrustStack showTicker={false} />
      <AsSeenInVillanytt />


      <Suspense fallback={<div style={{ minHeight: 400 }} />}>
        <EnergyCalculator />
        <HeroTrustBar />
        <Testimonials />
        <LiveTrustTicker />
        <TrustLogoBar />
        <LogoMarquee />
        <Brands />
        <ProcessSteps />
        <BentoWhy />
        <Services />
        <SofiasAnglar />
      </Suspense>
    </>
  );
}


